<template>
  <v-app id="app" :dir="dir" :lang="lang">
    <AppBar @show-feedback="showFeedbackDialog = true"></AppBar>

    <GlobalAlert></GlobalAlert>
    <router-view></router-view>

    <!-- Contact dialogs -->
    <ContactUs v-model="showFeedbackDialog" :title="shell.feedbackTitle"></ContactUs>
    <ContactUs v-model="showContactUsDialog" :title="shell.contactTitle"></ContactUs>

    <Footer
      v-if="!sharedBoard"
      @show-contact-us="showContactUsDialog = true"
    ></Footer>

    <HelpDrawer v-if="!sharedBoard" />
    <CoachMarkOverlay v-if="!sharedBoard" />
    <TeacherChecklistDialog v-if="!sharedBoard" />
    <TutorialVideoDialog v-if="!sharedBoard" />

    <v-snackbar
      v-model="studentJoinSnackbar"
      :timeout="6000"
      color="success"
      location="top"
    >
      {{ studentJoinSnackbarText }}
      <template #actions>
        <v-btn variant="text" @click="studentJoinSnackbar = false">{{ shell.dismiss }}</v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { computed, ref } from "vue";
import { onMounted } from "vue";
import useAxiosHelper from "./helpers/axiosHelper";
import { restoreSessionFromCookie } from "./composables/restoreSession";
import { useUserStore } from "./store/pinia/userStore";
import { useBoardDocumentTitle } from "./composables/useBoardDocumentTitle";
import { useEditModeStore } from "./store/pinia/editModeStore";
import { useStudentJoinNotifications } from "./composables/useStudentJoinNotifications";
import { useCookies } from "vue3-cookies";
import GlobalAlert from "./components/GlobalAlert.vue";
import ContactUs from "./components/ContactUs.vue";
import AppBar from "./components/AppBar.vue";
import Footer from "./components/Footer.vue";
import HelpDrawer from "./components/HelpDrawer.vue";
import CoachMarkOverlay from "./components/CoachMarkOverlay.vue";
import TeacherChecklistDialog from "./components/TeacherChecklistDialog.vue";
import TutorialVideoDialog from "./components/TutorialVideoDialog.vue";

import { defineAsyncComponent } from "vue";
import {
  installPracticeLocaleEffects,
  usePracticeLocale,
} from "./composables/usePracticeLocale";
import { shellUi } from "./i18n/practiceCopy";
const leftToolbar = defineAsyncComponent(
  () => import("./components/LeftToolbar.vue"),
);

installPracticeLocaleEffects();
const { dir, lang, isHebrew } = usePracticeLocale();
const shell = computed(() => shellUi(isHebrew.value));

const route = useRoute();
const sharedBoard = computed(() => route.name === "practiceTogether");
const cookies = useCookies().cookies;
const { initAxiosInterceptors } = useAxiosHelper();
const router = useRouter();
const userStore = useUserStore();
useBoardDocumentTitle();
const editModeStrore = useEditModeStore();
const { snackbar: studentJoinSnackbar, snackbarText: studentJoinSnackbarText } =
  useStudentJoinNotifications();

onMounted(async () => {
  initAxiosInterceptors();
  await restoreSessionFromCookie();
});

const showFeedbackDialog = ref(false);
const showContactUsDialog = ref(false);
</script>
<style>
html[lang="he"] body {
  font-family: "Noto Sans Hebrew", Roboto, "Helvetica Neue", Arial, sans-serif;
}

body {
  font-size: 16px;
  font-family: Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 400;
  color: #111;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

text {
  text-anchor: start;
  cursor: pointer;
  text-anchor: middle;
}

.mathboard-scroll,
.mathboard,
.cell-symbol-input,
.exponentInput {
  direction: ltr;
  unicode-bidi: isolate;
}

/* Hebrew UI uses Noto, which draws || as one heavy bar and shrinks ∫.
   Math on the board keeps Roboto for those glyphs and falls back to Noto for Hebrew letters. */
html[lang="he"] .mathboard,
html[lang="he"] .mathboard-scroll,
html[lang="he"] .board-text,
html[lang="he"] .freeText,
html[lang="he"] .sqrtsymbol,
html[lang="he"] .cell-symbol-input,
html[lang="he"] .exponentInput {
  font-family: Roboto, "Noto Sans Hebrew", "Helvetica Neue", Arial, sans-serif;
}

html {
  overflow-y: auto;
  overflow-x: auto;
  --keyboard-inset: 0px;
}

@media (max-width: 1023px) {
  html,
  body {
    touch-action: manipulation;
    overscroll-behavior: none;
  }

  #app {
    min-height: 100dvh;
  }
}
</style>
